import ky from "ky";
import { network } from "../config";
import { Project, TypeProjectParticipant } from "../types/Company";
import {
  ProjectRoles,
  ProjectTeamInvitation,
  ProjectTeamRole,
  TypeNewProject,
  TypeNewTag,
} from "../types/Projects";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import { SpecialistTag } from "../types/TypeSpecialist";
import { TypeUpdatedUserInfo } from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import { MultipartUploadPart } from "../utils/s3MultipartUpload";
import {
  UploadUrlRequest,
  UploadUrlResponse,
} from "../utils/storedFileReferences";
import customKy from "./kyExtend";

const { projects } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "projects/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class ProjectApi {
  static async createProject(
    token: string,
    payload: { companyId: string; newProject: TypeNewProject },
  ): Promise<TypeDefaultResponse & { project: Project }> {
    return await customKy
      .post(url(baseURL, projects.createProject), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async update(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { project: Project }> {
    return await customKy
      .patch(url(baseURL, projects.update, { id: updateData._id }), {
        headers: getAuthHeader(token),
        json: { updateData },
      })
      .then((res) => res.json());
  }

  static async deleteProject(
    token: string,
    projectId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, projects.deleteProject, { id: projectId }), {
        headers: getAuthHeader(token),
        json: { projectId },
      })
      .then((res) => res.json());
  }

  static async getProjectById(
    token: string,
    projectId: string,
  ): Promise<Project | null> {
    return await customKy
      .get(url(baseURL, projects.getProjectById, { id: projectId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async inviteUserToProject(
    token: string,
    invitations: {
      email: string;
      roles: ProjectRoles[];
      specializationIds?: string[];
    }[],
    projectId: string,
  ): Promise<
    TypeDefaultResponse & {
      successInvites?: (TypeDefaultResponse & { email: string })[];
      failedInvites?: (TypeDefaultResponse & { email: string })[];
    }
  > {
    return await customKy
      .post(url(baseURL, projects.inviteUserToProject, { id: projectId }), {
        headers: getAuthHeader(token),
        json: { invitations },
      })
      .then((res) => res.json());
  }

  static async addSpecialistsToProject(
    token: string,
    specialistIds: string[],
    projectId: string,
  ): Promise<TypeDefaultResponse> {
    return await ky
      .post(url(baseURL, projects.addSpecialistsToProject, { id: projectId }), {
        headers: getAuthHeader(token),
        json: { specialistIds },
      })
      .then((res) => res.json());
  }

  static async removeUserFromProject(
    token: string,
    payload: { userIdToRemove: string; projectId: string },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(
        url(baseURL, projects.removeUserFromProject, { id: payload.projectId }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async getProjectParticipants(
    token: string,
    projectId: string,
  ): Promise<
    TypeDefaultResponse & { participants?: TypeProjectParticipant[] | null }
  > {
    return await customKy
      .get(url(baseURL, projects.getProjectParticipants, { id: projectId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async uploadVideoToProject(
    token: string,
    formData: FormData,
  ): Promise<TypeDefaultResponse> {
    return await ky
      .post(url(baseURL, projects.uploadVideoToProject), {
        headers: getAuthHeader(token),
        body: formData,
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async getProjectVideoUploadUrl(
    token: string,
    projectId: string,
    payload: UploadUrlRequest & { language: string },
  ): Promise<UploadUrlResponse> {
    return await customKy
      .post(url(baseURL, projects.getProjectVideoUploadUrl, { id: projectId }), {
        headers: getAuthHeader(token),
        json: payload,
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async initiateProjectVideoMultipartUpload(
    token: string,
    projectId: string,
    payload: UploadUrlRequest & { language: string },
  ): Promise<
    TypeDefaultResponse & {
      uploadId?: string;
      filepath?: string;
      language?: string;
    }
  > {
    return await customKy
      .post(
        url(baseURL, projects.initiateProjectVideoMultipartUpload, {
          id: projectId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
          timeout: 60000,
        },
      )
      .then((res) => res.json());
  }

  static async generateProjectVideoPartUrl(
    token: string,
    projectId: string,
    payload: {
      language: string;
      filepath: string;
      uploadId: string;
      partNumber: number;
    },
  ): Promise<TypeDefaultResponse & { url?: string; partNumber?: number }> {
    return await customKy
      .post(
        url(baseURL, projects.generateProjectVideoPartUrl, { id: projectId }),
        {
          headers: getAuthHeader(token),
          json: payload,
          timeout: 60000,
        },
      )
      .then((res) => res.json());
  }

  static async completeProjectVideoMultipartUpload(
    token: string,
    projectId: string,
    payload: {
      language: string;
      filepath: string;
      uploadId: string;
      parts: MultipartUploadPart[];
    },
  ): Promise<TypeDefaultResponse & { filepath?: string }> {
    return await customKy
      .put(
        url(baseURL, projects.completeProjectVideoMultipartUpload, {
          id: projectId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
          timeout: 60000,
        },
      )
      .then((res) => res.json());
  }

  static async saveProjectVideo(
    token: string,
    payload: {
      projectId: string;
      language: string;
      videoS3?: string;
      video?: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, projects.uploadVideoToProject), {
        headers: getAuthHeader(token),
        json: payload,
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async addProjectTags(
    token: string,
    payload: {
      tags: TypeNewTag[];
      projectId: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      tags?: SpecialistTag[];
      tagIds?: string[];
    }
  > {
    return await ky
      .post(url(baseURL, projects.addProjectTags, { id: payload.projectId }), {
        headers: getAuthHeader(token),
        json: payload,
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async switchInvitationWithLink(
    token: string,
    payload: {
      projectId: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      code?: string;
    }
  > {
    return await customKy
      .post(url(baseURL, projects.switchInvitationWithLink), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  //Team ─────────────────────────────────────────────────────────────────────

  static async inviteTeamMembers(
    token: string,
    payload: {
      projectId: string;
      invitedBy: string;
      invitations: { shortId: string; roles: ProjectTeamRole[] }[];
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(
        url(baseURL, projects.inviteTeamMembers, { id: payload.projectId }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async removeTeamMember(
    token: string,
    payload: {
      projectId: string;
      targetUserId: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(
        url(baseURL, projects.removeTeamMember, { id: payload.projectId }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async getTeamInvitationHistory(
    token: string,
    projectId: string,
  ): Promise<
    TypeDefaultResponse & {
      invitations?: ProjectTeamInvitation[];
    }
  > {
    return await customKy
      .get(
        url(baseURL, projects.getTeamInvitationHistory, {
          id: projectId,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async updateTeamMemberRoles(
    token: string,
    payload: {
      projectId: string;
      updates: { memberId: string; roles: string[] }[];
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .patch(
        url(baseURL, projects.updateTeamMemberRoles, {
          id: payload.projectId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }
}
